import React, { useMemo, useEffect } from 'react';
import Highcharts from 'highcharts/highstock';
import HighchartsReact from 'highcharts-react-official';
import HighchartsXrange from 'highcharts/modules/xrange';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);
import { RecommendedStrategy } from '@/domain/Simulations/Simulations';

// Initialize xrange module
if (typeof Highcharts === 'object') {
	HighchartsXrange(Highcharts);
}

// Helper function to get consistent colors for strategies
const getStrategyColor = (index: number): string => {
	const colors = [
		'#00698B',
		'#26B2CE',
		'#FFD98A',
		'#D79B2B',
		'#F06B6B',
		'#FF8B8B',
		'#1D2BA9',
		'#66FFE8',
		'#A7E7FF',
		'#3DCB88',
		'#FF6B00',
		'#009E73',
		'#C77CFF',
		'#7F00FF',
		'#00AEEF',
		'#F564E3',
		'#8C564B',
		'#17BECF',
		'#BCBD22',
		'#2CA02C',
		'#D62728',
		'#9467BD',
		'#FF9DA7',
		'#1F77B4',
		'#FFBF00',
		'#5D9CEC',
		'#48CFAE',
		'#F7A35C',
		'#B82E2E',
		'#6A3D9A',
	];
	return colors[index % colors.length];
};

interface SynchronizedChartsProps {
	// Props for ReadingsGraph
	readingsGraphOptions: any;
	strategiesChartRef: any;
	isSpinnerReadingVisible: boolean;

	// Props for StrategiesChart
	strategiesData?: Array<RecommendedStrategy>;
	dateFrom: string;
	dateTo: string;
	showStrategies: boolean;

	// Annotation mode
	annotationMode?: boolean;
}

const SynchronizedCharts: React.FC<SynchronizedChartsProps> = ({
	readingsGraphOptions,
	strategiesChartRef,
	isSpinnerReadingVisible,
	strategiesData,
	dateFrom,
	dateTo,
	showStrategies,
	annotationMode = false,
}) => {
	// Fixed height percentages - readings 75%, strategies 25%
	const readingsHeightPercent = 75;
	const strategiesHeightPercent = 25;

	// Process strategies data (same logic as StrategiesChart)
	const strategiesChartData = useMemo(() => {
		if (!strategiesData || strategiesData.length === 0 || !showStrategies) {
			return {
				inpNames: [],
				timestamps: [],
				series: [],
			};
		}

		// Get all unique timestamps across all inpNames
		const allTimestamps = new Set<string>();
		strategiesData.forEach((item) => {
			Object.keys(item.strategies).forEach((timestamp) => {
				allTimestamps.add(timestamp);
			});
		});

		// Sort timestamps
		const timestamps = Array.from(allTimestamps).sort();

		// Convert timestamps to milliseconds
		const timestampsInMs = timestamps.map((timestamp) => {
			const year = parseInt(timestamp.substring(0, 4));
			const month = parseInt(timestamp.substring(4, 6)) - 1; // Month is 0-indexed
			const day = parseInt(timestamp.substring(6, 8));
			const hour = parseInt(timestamp.substring(8, 10));
			const minute = parseInt(timestamp.substring(10, 12));
			const second = parseInt(timestamp.substring(12, 14));
			// ✅ Extract milliseconds if they exist (characters 14-17)
			const milliseconds = timestamp.length > 14 ? parseInt(timestamp.substring(14, 17)) : 0;

			// ✅ Use Date.UTC to create timestamp in UTC without timezone conversion
			const date = Date.UTC(year, month, day, hour, minute, second, milliseconds);
			return date;
		});

		// inpNames for X axis
		const inpNames = strategiesData.map((item) => item.inpName);

		// Get all unique strategy names (preserve order)
		const allStrategyNames: string[] = [];
		const seenStrategies = new Set<string>();

		strategiesData.forEach((item) => {
			Object.keys(item.strategies).forEach((timestamp) => {
				const strategyData = item.strategies[timestamp];
				if (strategyData.strategy && !seenStrategies.has(strategyData.strategy)) {
					const strategyName = String(strategyData.strategy);
					allStrategyNames.push(strategyName);
					seenStrategies.add(strategyName);
				}
			});
		});

		const strategyNames = allStrategyNames;

		// Create series with time ranges
		const series = strategyNames.map((strategyName) => {
			const data: Array<any> = [];

			strategiesData.forEach((item, inpIndex) => {
				const allInpTimestamps = Object.keys(item.strategies).sort();
				let rangeStart: number | null = null;

				allInpTimestamps.forEach((timestamp, idx) => {
					const strategyData = item.strategies[timestamp];
					const timestampIndex = timestamps.indexOf(timestamp);
					const currentMs = timestampsInMs[timestampIndex];

					const hasStrategy = strategyData && strategyData.strategy === strategyName;

					if (hasStrategy) {
						if (rangeStart === null) {
							rangeStart = currentMs;
						}
					} else {
						if (rangeStart !== null) {
							data.push({
								x: inpIndex,
								low: rangeStart,
								high: currentMs,
								inpName: item.inpName,
								strategyName: strategyName,
							});
							rangeStart = null;
						}
					}
				});

				// Add the last range with +15 minutes
				if (rangeStart !== null) {
					const lastTimestamp = allInpTimestamps[allInpTimestamps.length - 1];
					const lastTimestampIndex = timestamps.indexOf(lastTimestamp);
					const lastMs = timestampsInMs[lastTimestampIndex];
					const fifteenMinutes = 15 * 60 * 1000;

					data.push({
						x: inpIndex,
						low: rangeStart,
						high: lastMs + fifteenMinutes,
						inpName: item.inpName,
						strategyName: strategyName,
					});
				}
			});

			return {
				name: strategyName,
				data: data,
			};
		});

		return {
			inpNames,
			timestamps: timestampsInMs,
			series,
		};
	}, [strategiesData, showStrategies]);

	// Combine both charts into a single stockChart with multiple yAxis
	const combinedChartOptions = useMemo<Highcharts.Options | null>(() => {
		if (!readingsGraphOptions) return null;

		// Extract local time components and treat as UTC to match chart data format
		const dFrom = dayjs(dateFrom);
		const dTo = dayjs(dateTo);
		const initialMin = Date.UTC(dFrom.year(), dFrom.month(), dFrom.date(), dFrom.hour(), dFrom.minute(), dFrom.second());
		const initialMax = Date.UTC(dTo.year(), dTo.month(), dTo.date(), dTo.hour(), dTo.minute(), dTo.second());

		// Build yAxis array
		const yAxis: any[] = [];

		// Calculate dynamic heights based on readingsHeightPercent state
		const strategiesHeightPercent = 100 - readingsHeightPercent;

		// First yAxis for readings (main chart) - dynamic height
		
		const readingsYAxis = Array.isArray(readingsGraphOptions.yAxis)
			? readingsGraphOptions.yAxis
			: [readingsGraphOptions.yAxis];

		// Add all readings yAxis with adjusted height
		readingsYAxis.forEach((axis: any, index: number) => {
			yAxis.push({
				...axis,
				height: showStrategies ? `${readingsHeightPercent}%` : '100%',
				top: '0%',
			});
		});

		// Second yAxis for strategies (bottom chart) - dynamic height
		// This will show the model names (categories) on the LEFT side
		if (showStrategies && strategiesChartData.series.length > 0) {
			yAxis.push({
				categories: strategiesChartData.inpNames,
				labels: {
					align: 'left',
					x: 5,
					crosshair: false,
					style: {
						fontSize: '11px',
						color: '#374151',
						fontWeight: '500',
						verticalAlign: 'middle',
					},
					useHTML: true,
					formatter: function (this: any) {
						return `<span style="
							background-color: rgba(255, 255, 255, 0.9);
							padding: 2px 6px;
							border-radius: 3px;
							box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
							display: inline-block;
							font-size: 13px;
							color: #374151;
							font-weight: 500;
						">${this.value}</span>`;
					},
				},
				title: {
					text: undefined,
				},
				top: `${readingsHeightPercent}%`,
				height: `${strategiesHeightPercent}%`,
				offset: 0,
				lineWidth: 1,
				gridLineColor: '#E5E7EB',
				reversed: false,
				opposite: false,
			});
		}

		// Build series array
		const series: any[] = [];

		// Add readings series (preserve their original yAxis indices)
		if (readingsGraphOptions.series) {
			
			readingsGraphOptions.series.forEach((s: any) => {
				series.push({
					...s,
					yAxis: s.yAxis !== undefined ? s.yAxis : 0,
					xAxis: 0,
					showInLegend: false,
				});
			});
		}

		// Add strategies series (use last yAxis, xAxis 0 - shared datetime axis)
		// For xrange series: x = datetime start, x2 = datetime end, y = category index
		if (showStrategies && strategiesChartData.series.length > 0) {
			const strategiesYAxisIndex = yAxis.length - 1; // Last yAxis is for strategies

			strategiesChartData.series.forEach((serie: any, index: number) => {
				const strategyColor = getStrategyColor(index);

				series.push({
					type: 'xrange',
					name: serie.name,
					data: serie.data.map((point: any) => ({
						// x = start datetime, x2 = end datetime
						x: point.low,
						x2: point.high,
						// y = model index (category)
						y: point.x,
						inpName: point.inpName,
						strategyName: point.strategyName,
						color: strategyColor,
					})),
					color: strategyColor,
					colorByPoint: false,
					yAxis: strategiesYAxisIndex,
					xAxis: 0,
					grouping: false,
					dataLabels: {
						enabled: false,
					},
					borderWidth: 0,
					borderRadius: 3,
					pointWidth: 25,
					showInLegend: true,
				});
			});
		}

		return {
			...readingsGraphOptions,
			chart: {
				...readingsGraphOptions.chart,
				type: 'line',
				animation: false,
				style: {
					overflow: 'visible',
					cursor: annotationMode ? 'crosshair' : 'default',
				},
				cursor: annotationMode ? 'crosshair' : 'default',
			},
			xAxis: readingsGraphOptions.xAxis,
			yAxis: yAxis,
			series: series,
			tooltip: {
				...readingsGraphOptions.tooltip,
				crosshairs: false,
				split: false,
				hideDelay: 0,
			},
			legend: {
				enabled: true,
				align: 'center',
				verticalAlign: 'bottom',
				layout: 'horizontal',
				itemStyle: {
					fontSize: '11px',
					color: '#374151',
					fontWeight: '400',
					cursor: 'default',
				},
				itemHoverStyle: {
					color: '#1F2937',
				},
				margin: 15,
				padding: 10,
				events: {
					itemClick: function () {
						return false;
					},
				},
			},
			plotOptions: {
				...readingsGraphOptions.plotOptions,
				xrange: {
					grouping: false,
					stacking: undefined,
				},
				series: {
					...readingsGraphOptions.plotOptions?.series,
					cursor: annotationMode ? 'crosshair' : 'default',
				},
			},
		};
	}, [
		readingsGraphOptions,
		strategiesChartData,
		showStrategies,
		dateFrom,
		dateTo,
		readingsHeightPercent,
		annotationMode,
	]);

	if (!combinedChartOptions) return null;

	return (
		<div style={{ width: '100%', height: '100%', position: 'relative', overflow: 'visible' }}>
			<HighchartsReact
				highcharts={Highcharts}
				constructorType={'stockChart'}
				options={combinedChartOptions}
				ref={strategiesChartRef}
				immutable={false}
				containerProps={{ style: { height: '100%', width: '100%', overflow: 'visible' } }}
			/>
		</div>
	);
};

export default SynchronizedCharts;
